import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import MyApiPlan from "../../components/setting/MyApiPlan";
import SubscriptionStatus from "../pages/SubscriptionStatus";
import { getEntitlement } from "../../store/actions/entitlementAction";
import { planCardData } from "../../utils/data";
import SpinnerIcon from "../../components/global/SpinnerIcon";

/*
    PLAN — what the customer is on, and the way out of it.

    THE PLAN COMES FROM THE SERVER, NOT FROM A CACHED COLUMN (backend#103).
    This is carried over from views/pages/Setting.js wholesale, because it is
    the answer to a bug that has been fixed and un-fixed twice (static#79,
    #84, #85). The old page decided everything from `user.stripe_customer_id`,
    a copy of a database column taken at sign-in and kept in localStorage: it
    is blank for all 776 free-plan customers and for comped accounts, and
    stale for anyone who paid after their last sign-in — so a paying customer
    was shown the free plan with no way to cancel.

    Five states, not two, and the fifth is not a plan state:
      null         the request failed; say so, show no plan at all
      "none"       never chose a plan (443 accounts)
      free/paid    a plan we can describe from planCardData
      unrecognised a plan with no card — MyApiPlan's own branch
      + status     handled by SubscriptionStatus, the ONLY route to
                   /main/cancel-membership anywhere in the app

    Folding the failure case into "none" is what #87's review caught: it told
    a paying customer they had no plan whenever the endpoint blipped.
*/
const Plan = () => {
  const dispatch = useDispatch();
  // -1 is "still loading". null is "we asked and could not get an answer".
  const [entitlement, setEntitlement] = useState(-1);

  const loadEntitlement = useCallback(
    () => dispatch(getEntitlement((res) => setEntitlement(res))),
    [dispatch]
  );

  useEffect(() => {
    // Always asked. There is no condition under which the server should not
    // be allowed to answer — that condition was the bug.
    loadEntitlement();
  }, [loadEntitlement]);

  const planKey = entitlement?.plan?.key;
  const apiPlan = planCardData?.find(
    (ele) => planKey && ele?.id === `api-${planKey}`
  );

  return (
    <>
      <h1 className="account__heading">API Plan</h1>

      <div className="account__panel">
        {entitlement === -1 ? (
          <SpinnerIcon label="Loading your plan" />
        ) : !entitlement ? (
          <>
            <p className="account__fact account__fact--strong">
              We couldn't load your plan
            </p>
            <p className="account__fact mb-0">
              Something went wrong on our end, so we're not showing your plan
              rather than showing the wrong one. Refresh to try again — nothing
              about your subscription has changed.
            </p>
          </>
        ) : entitlement.status === "none" ? (
          <>
            <p className="account__fact account__fact--strong">
              No API plan yet
            </p>
            <p className="account__fact">
              Choose a plan to start using the Golfbert API.
            </p>
            <Link to="/api/plans" className="link-to">
              See the API plans
            </Link>
          </>
        ) : (
          <>
            <MyApiPlan apiPlan={apiPlan} planLabel={entitlement?.plan?.label} />
            {/*
                Gated on having a plan, not on having a Stripe record. That
                gate is what hid the cancel link from paying customers.
            */}
            <SubscriptionStatus
              entitlement={entitlement}
              onResumed={loadEntitlement}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Plan;
